import React, { useState } from "react";
import useSWR from "swr";
import api, { money2, fmtDate } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInputES } from "@/components/ui/DateInputES";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusBadge from "@/components/StatusBadge";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { PAY_METHOD, PAY_KIND } from "@/lib/labels";

const fetcher = (u) => api.get(u).then((r) => r.data);
const empty = { participant_id: "", tour_id: "", amount: 0, currency: "USD", exchange_rate: 1, method: "transfer", date: new Date().toISOString().slice(0,10), kind: "deposit", is_refund: false, reference: "" };

export default function Payments() {
  const { data: pays, mutate } = useSWR("/payments", fetcher);
  const { data: parts } = useSWR("/participants", fetcher);
  const { data: tours } = useSWR("/tours", fetcher);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const partName = (id) => { const p = parts?.find((x) => x.id === id); return p ? `${p.first_name} ${p.last_name}` : "—"; };
  const tourName = (id) => tours?.find((t) => t.id === id)?.name || "—";

  const submit = async () => {
    if (!form.participant_id) return toast.error("Selecciona un participante");
    try {
      await api.post("/payments", { ...form, amount: Number(form.amount), exchange_rate: Number(form.exchange_rate) });
      toast.success("Pago registrado");
      setForm(empty); setOpen(false); mutate();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const onSelectPart = (id) => {
    const p = parts?.find((x) => x.id === id);
    setForm((f) => ({ ...f, participant_id: id, tour_id: p?.tour_id || "" }));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Pagos</h1><p className="text-slate-500 mt-1">Cada pago se registra como operación independiente (fecha, importe, moneda, método).</p></div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-payment-btn" className="bg-[#026FCB] hover:bg-[#0358a5]"><Plus size={16} className="mr-2" />Registrar pago</Button></DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle>Nuevo pago</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2"><Label>Participante</Label>
                <Select value={form.participant_id} onValueChange={onSelectPart}>
                  <SelectTrigger data-testid="pay-participant"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                  <SelectContent>{(parts || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name} — {tourName(p.tour_id)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Importe</Label><Input type="number" data-testid="pay-amount" value={form.amount} onChange={(e) => setForm({...form, amount: e.target.value})} /></div>
              <div><Label>Moneda</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({...form, currency: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["USD","EUR","CLP","NOK","ISK","GBP","MXN"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Tipo de cambio a USD</Label><Input type="number" step="0.0001" value={form.exchange_rate} onChange={(e) => setForm({...form, exchange_rate: e.target.value})} /></div>
              <div><Label>Fecha</Label><DateInputES value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} /></div>
              <div><Label>Tipo</Label>
                <Select value={form.kind} onValueChange={(v) => setForm({...form, kind: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["deposit","second","final","extra","refund"].map((k) => <SelectItem key={k} value={k}>{PAY_KIND[k]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Método</Label>
                <Select value={form.method} onValueChange={(v) => setForm({...form, method: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["stripe","transfer","cash","card","paypal","other"].map((m) => <SelectItem key={m} value={m}>{PAY_METHOD[m]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Referencia</Label><Input value={form.reference} onChange={(e) => setForm({...form, reference: e.target.value})} /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button data-testid="pay-save" className="bg-[#026FCB] hover:bg-[#0358a5]" onClick={submit}>Registrar</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="overflow-hidden"><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-100 dark:bg-[#1A2336]">
            <tr>
              <th className="text-left px-4 py-3">Fecha</th>
              <th className="text-left px-4 py-3">Participante</th>
              <th className="text-left px-4 py-3">Tour</th>
              <th className="text-left px-4 py-3">Tipo</th>
              <th className="text-left px-4 py-3">Método</th>
              <th className="text-right px-4 py-3">Importe</th>
              <th className="text-right px-4 py-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {(pays || []).map((p) => (
              <tr key={p.id} className="border-t border-slate-100 dark:border-[#25334D]/50">
                <td className="px-4 py-3 mono">{fmtDate(p.date)}</td>
                <td className="px-4 py-3 font-medium">{partName(p.participant_id)}</td>
                <td className="px-4 py-3 text-slate-500">{tourName(p.tour_id)}</td>
                <td className="px-4 py-3"><StatusBadge value={p.kind === "refund" ? "cancelled" : "confirmed"} label={PAY_KIND[p.kind] || p.kind} /></td>
                <td className="px-4 py-3 text-slate-500">{PAY_METHOD[p.method] || p.method}</td>
                <td className={`px-4 py-3 text-right mono ${p.is_refund ? "text-red-600" : ""}`}>{p.is_refund ? "-" : ""}{money2(p.amount, p.currency)}</td>
                <td className="px-4 py-3 text-right">
                  <Button
                    size="sm"
                    variant="ghost"
                    data-testid={`delete-pay-${p.id}`}
                    className="h-8 w-8 p-0 text-slate-400 hover:text-red-500"
                    title="Borrar pago"
                    onClick={async () => {
                      const label = `${money2(p.amount, p.currency)} · ${fmtDate(p.date)} · ${partName(p.participant_id)}`;
                      if (!window.confirm(`¿Borrar este pago?\n\n${label}\n\nEsta acción no se puede deshacer.`)) return;
                      try {
                        await api.delete(`/payments/${p.id}`);
                        toast.success("Pago borrado");
                        mutate();
                        if (p.participant_id) {
                          const { mutate: gMutate } = await import("swr");
                          gMutate.default?.(`/bookings/${p.participant_id}/plan`);
                        }
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "No se pudo borrar el pago");
                      }
                    }}
                  >
                    <Trash2 size={14} />
                  </Button>
                </td>
              </tr>
            ))}
            {(!pays || pays.length === 0) && <tr><td colSpan={7} className="text-center py-10 text-slate-500">Sin pagos registrados.</td></tr>}
          </tbody>
        </table>
      </CardContent></Card>
    </div>
  );
}
